import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submit"]
  // Opt-in: when true, the submit stays disabled until the form is dirty AND
  // passes native HTML validation (`required` fields filled, etc.).
  // startValidated: when true, evaluate validity on connect and enable the submit
  // immediately if the form is already valid (e.g. server-rendered/voice prefill,
  // where no input event fires to trip the dirty check).
  static values = { requireValid: Boolean, startValidated: Boolean }

  connect() {
    this._snapshot = this._captureValues()
    this._disableSubmit()
    this.element.addEventListener("input", this._check)
    this.element.addEventListener("change", this._check)
    if (this.startValidatedValue && this._valid()) this._enableSubmit()
  }

  disconnect() {
    this.element.removeEventListener("input", this._check)
    this.element.removeEventListener("change", this._check)
  }

  // Something this screen owns changed outside the fields — the budget order,
  // which saves on drop. The submit has to come alive anyway: a Save that
  // stays greyed after a visible change reads as "that did not register".
  // Sticky, so editing a field and undoing it does not grey Save again.
  markDirty() {
    this._forcedDirty = true
    if (this._valid()) this._enableSubmit()
  }

  // ── Private ──────────────────────────────────────────────

  _check = () => {
    const current = this._captureValues()
    const dirty = this._forcedDirty || Object.keys(this._snapshot).some(
      key => this._snapshot[key] !== current[key]
    )

    if (dirty && this._valid()) {
      this._enableSubmit()
    } else {
      this._disableSubmit()
    }
  }

  _valid() {
    if (!this.requireValidValue) return true
    return this.element.checkValidity()
  }

  _captureValues() {
    const values = {}
    const fields = this.element.querySelectorAll("input, select, textarea")

    fields.forEach(field => {
      if (this._isIgnored(field)) return

      const key = field.name || field.id
      if (!key) return

      if (field.type === "checkbox" || field.type === "radio") {
        values[key + ":" + field.value] = field.checked
      } else {
        values[key] = field.value
      }
    })

    return values
  }

  _isIgnored(field) {
    return field.name === "authenticity_token" ||
           field.name === "_method" ||
           field.type === "submit"
  }

  _disableSubmit() {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = true
    this.submitTarget.classList.add("opacity-40", "cursor-not-allowed")
  }

  _enableSubmit() {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = false
    this.submitTarget.classList.remove("opacity-40", "cursor-not-allowed")
  }
}
